import React, { createContext, useContext, useEffect, useState } from 'react';
import Loading from '../components/Loading';
import api from '../utilities/api';
import { getCookie, clearCookie } from '../utilities/cookies';

const authorizeContext = createContext();

const AuthorizationProvider = ({ children }) => {
    const isLoginPage = typeof window !== 'undefined' && window.location?.pathname === '/login';
    const [content, setContent] = useState(
        isLoginPage ? children : <Loading message='Please wait, logging you in...' />
    );
    const [user, setUser] = useState({});
    //const { showError } = useMessage();

    const authorize = async (loggedIn, cb) => {
        const currentPath = typeof window !== 'undefined' ? window.location?.pathname : '';

        if (loggedIn) {
            // If already logged in and currently on /login, send to dashboard
            if (currentPath === '/login') {
                setContent(
                    <Loading message='Redirecting to Dashboard' redirectTo='/' />
                );
            } else {
                setContent(children);
            }
        } else {
            // Not logged in: allow the /login route to render without redirect
            if (currentPath === '/login') {
                setContent(children);
            } else {
                setContent(
                    <Loading
                        message='Please wait, redirecting you to Login'
                        redirectTo={'/login'}
                    />
                );
            }
        }
        if (typeof cb === 'function') cb(setUser);
    };

    useEffect(() => {
        (async () => {
            try {
                const accessToken = getCookie('accessToken');
                if (!accessToken) {
                    authorize(false);
                    return;
                }

                const response = await api.get('/api/auth/me', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const { user , accessToken: token } = response.data || {};

                if (user && (user.role === 'admin')) {
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('accessToken', JSON.stringify(token));
                    authorize(true, (setUser) => setUser(user));
                } else {
                    // Not an admin or invalid token
                    localStorage.clear();
                    clearCookie('accessToken');
                    authorize(false);
                }
            } catch (err) {
                console.log(err);
                localStorage.clear();
                clearCookie('accessToken');
                authorize(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <authorizeContext.Provider value={{ authorize, setUser, user, setContent }}>
            {content}
        </authorizeContext.Provider>
    );
};

const useAuthorize = () => useContext(authorizeContext).authorize;
const useUser = () => useContext(authorizeContext)?.user;
const useSetUser = () => useContext(authorizeContext).setUser;
const useSetContent = () => useContext(authorizeContext).setContent;

export default AuthorizationProvider;
export { useAuthorize, useUser, useSetUser, useSetContent };
